import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import React, { useState } from 'react';
import type OriginTracing from '@atlassiansox/origin-tracing';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import { type Template, TemplateType } from '../../../../containers/template/types';
import TemplateCard from './template-card';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	marginTop: token('space.200', '16px'),
});

interface Props {
	templates: Template[];
	activeTemplate?: Template;
	onTemplateClick?: (template: Template) => void;
	onTemplateMouseEnter?: (template: Template) => void;
	onMouseLeave?: (e: React.MouseEvent<HTMLDivElement>) => void;
	hoverRef?: React.Ref<any>;
	isDisabled: boolean;
	origin: OriginTracing | null | undefined;
}

const TemplateList = ({
	onTemplateClick,
	onTemplateMouseEnter,
	onMouseLeave,
	hoverRef,
	templates,
	activeTemplate,
	isDisabled,
	origin,
}: Props) => {
	const [hoveredTemplate, setHoveredTemplate] = useState<Template | null>(null);

	const handleClick =
		(template: Template) => (_: React.MouseEvent, analyticsEvent?: UIAnalyticsEvent) => {
			const originAttributes = origin?.toAnalyticsAttributes({
				hasGeneratedId: true,
			});

			if (analyticsEvent) {
				fireUIAnalytics(analyticsEvent, `${template.key}TemplateButton`, originAttributes ?? {});
			}
			onTemplateClick && onTemplateClick(template);
		};
	const handleMouseEnter = (template: Template) => () => {
		onTemplateMouseEnter && onTemplateMouseEnter(template);
		setHoveredTemplate(template);
	};

	return (
		<Wrapper onMouseLeave={onMouseLeave}>
			{templates.map<React.ReactNode>((tmpl) => (
				<div
					key={tmpl.key}
					ref={
						hoveredTemplate &&
						hoveredTemplate.type === TemplateType.Confluence &&
						hoveredTemplate.key === tmpl.key
							? hoverRef
							: undefined
					}
				>
					<TemplateCard
						name={tmpl.name}
						description={tmpl.description}
						icon={tmpl.iconRender({
							size: 'medium',
							color: tmpl.defaultIconColor,
						})}
						onClick={handleClick(tmpl)}
						onMouseEnter={handleMouseEnter(tmpl)}
						tooltip={tmpl.tooltip}
						isDisabled={isDisabled}
						isActive={tmpl.key === activeTemplate?.key}
						renderShortcutIcon={tmpl.type === TemplateType.Link && tmpl.renderShortcutIcon}
					/>
				</div>
			))}
		</Wrapper>
	);
};

export default TemplateList;
